import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { PeriodSelector } from "../ui-components/PeriodSelector.jsx";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import GeneralSubmenu from "../ui-components/GeneralSubmenu.jsx";
import { HeaderPortal } from "../ui-components/HeaderSlot.jsx";
import { secondaryButtonCls } from "../ui-components/buttonStyles.js";
import PropTypes from "prop-types";

// Shared governance secondary-button style (see buttonStyles.js), identical to the
// secondary buttons on /approvals/:id and /control-policies/approval-policies/:id.
const secondaryBtnCls = secondaryButtonCls;

// Page tabs now live in the global TopBar (app header) via headerTabs.js.
// This component is reduced to the per-page sub-toolbar: period selector,
// filters, and report edit-mode controls — borderless, no title/back/search.
const AdministrationHeader = (props) => {
	const location = useLocation();
	const { id } = useParams();

	// Use Zustand store for period selector with dayjs dates
	const { startDate, setStartDate, endDate, setEndDate } = useFilterStoreWithDayjsDates();

	// State for filter dropdown
	const [currentFilter, setCurrentFilter] = useState("all");

	const handleFilterChange = (filter) => {
		setCurrentFilter(filter);
	};

	const isReportEditor = location.pathname.substring(0, 14) === "/report-editor";
	const showPeriod = props.showPeriodSelector !== false;

	// When the period selector is hidden and this isn't the report editor, the
	// GeneralSubmenu renders nothing and there's no content — skip the empty bar.
	if (!showPeriod && !isReportEditor) {
		return null;
	}

	// Report editor: render the controls in the global TopBar (like
	// /control-policies/approval-policies/:id) instead of a separate sub-toolbar.
	// Order: period selector → edit-mode button → dropdown menu.
	if (isReportEditor) {
		// Period selector is a context control → row 2 (right); the edit-mode
		// toggle + view/schedule menu are actions → row 1 (default slot).
		return (
			<>
				{props.showPeriodSelector !== false && (
					<HeaderPortal slot="row2-right">
						<PeriodSelector
							compact
							startDate={startDate}
							endDate={endDate}
							setStartDate={setStartDate}
							setEndDate={setEndDate}
							reportTimePeriod={props.reportTimePeriod}
							onTimePeriodChange={props.onTimePeriodChange}
							isEditMode={props.isEditMode}
							showTimePeriodPrompt={props.showTimePeriodPrompt}
							onApplyTimePeriodToReport={props.onApplyTimePeriodToReport}
							onDismissTimePeriodPrompt={props.onDismissTimePeriodPrompt}
						/>
					</HeaderPortal>
				)}
				<HeaderPortal>
					<div className="flex items-center gap-2.5">
						{!props.isEditMode ? (
							<button
								onClick={() => props.onEditModeToggle(true)}
								type="button"
								className={secondaryBtnCls}
								title="Edit Report"
							>
								Edit
							</button>
						) : (
							<button
								onClick={() => props.onEditModeToggle(false)}
								type="button"
								className={secondaryBtnCls}
								title="Save Layout"
							>
								Done
							</button>
						)}
						<GeneralSubmenu
							onFilterChange={handleFilterChange}
							currentFilter={currentFilter}
							reportId={id}
							reportName={props.reportName}
							viewFilters={props.viewFilters}
							onSchedule={props.onSchedule}
							triggerClassName={secondaryBtnCls}
						/>
					</div>
				</HeaderPortal>
			</>
		);
	}

	// Non-editor pages that opt into the period selector (currently the data-pipeline
	// Activity Log tab) render it on row 2 (right), beside the page tabs.
	return (
		<HeaderPortal slot="row2-right">
			<div className="flex items-center gap-1">
				<PeriodSelector
					compact
					startDate={startDate}
					endDate={endDate}
					setStartDate={setStartDate}
					setEndDate={setEndDate}
				/>
			</div>
		</HeaderPortal>
	);
};

AdministrationHeader.propTypes = {
	title: PropTypes.string,
	className: PropTypes.string,
	filters: PropTypes.array,
	setFilters: PropTypes.func,
	showSearch: PropTypes.bool,
	showPeriodSelector: PropTypes.bool,
	excludeCategories: PropTypes.array,
	isEditMode: PropTypes.bool,
	onEditModeToggle: PropTypes.func,
	reportName: PropTypes.string,
	reportTimePeriod: PropTypes.shape({
		start_date: PropTypes.string,
		end_date: PropTypes.string,
	}),
	onTimePeriodChange: PropTypes.func,
	showTimePeriodPrompt: PropTypes.bool,
	onApplyTimePeriodToReport: PropTypes.func,
	onDismissTimePeriodPrompt: PropTypes.func,
	viewFilters: PropTypes.array,
	onSchedule: PropTypes.func,
};

export default AdministrationHeader;
